import { gql } from "@apollo/client";
const MENU_FIELDS = gql`
    fragment MenuFields on CMSMenu {
        id
        meta {
            title
            url
            search
        }
        items {
            id
            url
            title {
                rendered
            }
            parent
        }
    }
`;
const FEATURED_IMAGE_FIELDS = gql`
    fragment FeaturedImageFields on CMSAttachment {
        sourceUrl: source_url
        altText: alt_text
        caption {
            raw
        }
        mediaDetails: media_details {
            height
            width
            sizes {
                small {
                    sourceUrl: source_url
                    width
                    height
                }
                medium {
                    sourceUrl: source_url
                    width
                    height
                }
                large {
                    sourceUrl: source_url
                    width
                    height
                }
                full {
                    sourceUrl: source_url
                    width
                    height
                }
            }
        }
    }
`;
const CONTRIBUTOR_FIELDS = gql`
    fragment ContributorFields on CMSUser {
        id
        name
        avatar
        link
        jobTitle
        description
        expertise
        socialLinks {
            linkedin
            x
            youtube
        }
    }
`;
const POST_FIELDS = gql`
    ${MENU_FIELDS}
    ${FEATURED_IMAGE_FIELDS}
    ${CONTRIBUTOR_FIELDS}
    fragment RevisionFields on CMSRevision {
        id
        title {
            raw
        }
        slug
        blocks: cms_blocks
        menu {
            ...MenuFields
        }
    }
    fragment PostFields on CMSPost {
        id
        head: yoast_head
        title {
            raw
        }
        excerpt {
            raw
        }
        type
        link
        slug
        status
        date
        author {
            ...ContributorFields
        }
        readTime
        featuredImage {
            ...FeaturedImageFields
        }
        blocks: cms_blocks
        categories {
            id
            name
            link
            slug
            parent
        }
        primaryCategory {
            id
            name
            slug
            link
        }
        pageIdentifiers {
            id
            name
            link
            slug
            parent
        }
        postTags {
            id
            name
            slug
            link
        }
        menu {
            ...MenuFields
        }
        sidebar {
            blocks: cms_blocks
        }
        footer {
            blocks: cms_blocks
        }
        revisions(per_page: "1") @include(if: $withRevisions) {
            ...RevisionFields
        }
        relatedPosts: related {
            ... on CMSPost {
                id
                link
                title {
                    raw
                }
                excerpt {
                    raw
                }
                primaryCategory {
                    name
                    link
                }
                categories {
                    name
                    link
                }
                featuredImage {
                    ...FeaturedImageFields
                }
            }
        }
        meta {
            editor {
                ...ContributorFields
            }
            reviewedBy {
                ...ContributorFields
            }
            aiSummaryBullets
            aiSummaryParagraph
            aiSummaryDisplayType
        }
    }
    fragment PageFields on CMSPage {
        id
        head: yoast_head
        title {
            raw
        }
        type
        link
        slug
        status
        date
        menu {
            ...MenuFields
        }
        blocks: cms_blocks
        sidebar {
            blocks: cms_blocks
        }
        footer {
            blocks: cms_blocks
        }
        header {
            disabled
            transparent
        }
        pageIdentifiers {
            id
            name
            link
            slug
            parent
        }
        revisions(per_page: "1") @include(if: $withRevisions) {
            ...RevisionFields
        }
    }
`;
const POST_QUERY = gql`
    ${POST_FIELDS}
    ${MENU_FIELDS}
    query PostQuery(
        $site: CMSSite
        $id: ID!
        $idType: CMSIDType!
        $postType: CMSPostTypeEnum!
        $withRevisions: Boolean!
        $withSettings: Boolean!
    ) {
        cmsSettings(site: $site) @include(if: $withSettings) {
            menu {
                ...MenuFields
            }
        }
        cmsPost(site: $site, id: $id, idType: $idType, post_type: $postType) {
            ...PostFields
            ...PageFields
        }
    }
`;
const STANDALONE_PREVIEW_QUERY = gql`
    query StandalonePreviewQuery(
        $site: CMSSite
        $id: ID!
        $idType: CMSIDType!
        $postType: CMSPostTypeEnum!
    ) {
        cmsPost(site: $site, id: $id, idType: $idType, post_type: $postType) {
            __typename
            ... on CMSSidebar {
                id
                status
                blocks: cms_blocks
                revisions(per_page: "1") {
                    blocks: cms_blocks
                }
            }
            ... on CMSFooter {
                id
                status
                blocks: cms_blocks
                revisions(per_page: "1") {
                    blocks: cms_blocks
                }
            }
        }
    }
`;
const POSTS_QUERY = gql`
    ${POST_FIELDS}
    query PostsQuery(
        $site: CMSSite
        $postType: CMSPostTypeEnum!
        $search: String
        $page: String
        $order: CMSPostOrder
        $orderBy: CMSPostOrderBy
        $perPage: String
        $taxonomies: [CMSTaxonomyInput]
        $withRevisions: Boolean!
    ) {
        cmsPosts(
            site: $site
            post_type: $postType
            search: $search
            page: $page
            order: $order
            orderby: $orderBy
            per_page: $perPage
            taxonomies: $taxonomies
        ) {
            total
            pages
            posts {
                ...PostFields
                ...PageFields
            }
        }
    }
`;
const POSTS_ARCHIVE_FIELDS = gql`
    ${FEATURED_IMAGE_FIELDS}
    fragment ArchiveFields on CMSPost {
        id
        title {
            raw
        }
        link
        ... on CMSPost {
            readTime
            excerpt {
                raw
            }
            categories {
                name
                link
                parent
            }
            primaryCategory {
                name
                link
                parent
            }
            featuredImage {
                ...FeaturedImageFields
            }
        }
    }
`;
const SEARCH_QUERY = gql`
    ${POSTS_ARCHIVE_FIELDS}
    ${MENU_FIELDS}
    query SearchQuery(
        $site: CMSSite
        $page: String
        $perPage: String
        $search: String
    ) {
        cmsPosts(
            site: $site
            post_type: POST
            page: $page
            per_page: $perPage
            search: $search
            orderby: RELEVANCE
        ) {
            total
            pages
            posts {
                ...ArchiveFields
            }
        }
        cmsSettings(site: $site) {
            menu {
                ...MenuFields
            }
            sidebar(placement: SEARCH) {
                blocks: cms_blocks
            }
        }
        cmsSearchHead(site: $site, searchTerm: $search) {
            head: yoast_head
        }
    }
`;
const ARCHIVE_MORE_QUERY = gql`
    ${POSTS_ARCHIVE_FIELDS}
    query ArchiveMoreQuery(
        $site: CMSSite
        $page: String
        $taxonomies: [CMSTaxonomyInput]
        $perPage: String
        $search: String
        $order: CMSPostOrder
        $orderBy: CMSPostOrderBy
    ) {
        cmsPosts(
            post_type: POST
            site: $site
            page: $page
            taxonomies: $taxonomies
            per_page: $perPage
            search: $search
            order: $order
            orderby: $orderBy
        ) {
            posts {
                ...ArchiveFields
            }
        }
    }
`;
const POSTS_BLOCK_QUERY = gql`
    ${FEATURED_IMAGE_FIELDS}
    query PostsBlockQuery(
        $site: CMSSite
        $postType: CMSPostTypeEnum!
        $page: String
        $order: CMSPostOrder
        $orderby: CMSPostOrderBy
        $perPage: String
        $include: [ID]
        $taxonomies: [CMSTaxonomyInput]
    ) {
        cmsPosts(
            site: $site
            post_type: $postType
            page: $page
            order: $order
            orderby: $orderby
            per_page: $perPage
            taxonomies: $taxonomies
            include: $include
        ) {
            posts {
                id
                title {
                    raw
                }
                link
                ... on CMSPost {
                    categories {
                        name
                        link
                    }
                    primaryCategory {
                        name
                        link
                    }
                    excerpt {
                        raw
                    }
                    readTime
                    featuredImage {
                        ...FeaturedImageFields
                    }
                }
            }
        }
    }
`;
export {
  ARCHIVE_MORE_QUERY,
  MENU_FIELDS,
  POSTS_ARCHIVE_FIELDS,
  POSTS_BLOCK_QUERY,
  POSTS_QUERY,
  POST_FIELDS,
  POST_QUERY,
  SEARCH_QUERY,
  STANDALONE_PREVIEW_QUERY
};
